import { ToastNotification } from '@carbon/react';
import { createPortal } from 'react-dom';

import style from './success-toast.module.scss';

const TIMEOUT = 5000;

interface Props {
  message: string;
  subtitle?: string;
  /**
   * Fired when the toast closes (the × button, and Carbon also calls it on an
   * auto-dismiss). Return `false` to VETO the hide; anything else lets it close.
   * With `timeout: 0` the × is the only dismissal path, so use this to keep the
   * caller's "toast showing" state in sync.
   */
  onClose?: () => boolean;
  /**
   * Auto-dismiss delay in ms. Defaults to 5s; pass `0` to keep the toast up until
   * the user closes it (Carbon's own default — its auto-close bails on a falsy
   * timeout). Mirrors the `ErrorToast` prop.
   */
  timeout?: number;
  /**
   * Downward pixel offset, so a second concurrent toast doesn't sit on top of the first (the
   * toast portals to `document.body` at a fixed position, so a parent can't reposition it).
   * Default 0 — the top slot, unchanged for every existing caller.
   */
  stackOffset?: number;
}

export function SuccessToast({
  message,
  subtitle,
  onClose,
  timeout = TIMEOUT,
  stackOffset,
}: Props) {
  return createPortal(
    <div
      className={style.successToast}
      style={
        stackOffset ? { transform: `translateY(${stackOffset}px)` } : undefined
      }
    >
      <ToastNotification
        title={message}
        kind="success"
        subtitle={subtitle}
        lowContrast
        onClose={onClose}
        timeout={timeout}
      />
    </div>,
    document.body
  );
}
